import React, { useState } from 'react';
import './ComparisonTable.css';
import ColorVariantSlideshow from './ColorVariantSlideshow';
import InfoPopup from './InfoPopup';
import type { ProductData } from '../types/productTypes';

interface ComparisonTableProps {
  products: (ProductData | null)[];
}

const ComparisonTable: React.FC<ComparisonTableProps> = ({ products }) => {
  // Always show 3 columns, create a properly positioned array
  const maxColumns = 3;
  const paddedProducts: (ProductData | null)[] = [];

  // Create array with proper positioning
  for (let i = 0; i < maxColumns; i++) {
    paddedProducts[i] = products[i] || null;
  }

  // State for feedback form
  const [feedbackText, setFeedbackText] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // This function could be expanded in the future to track selected colors
  // or update other parts of the UI based on color selection
  const handleColorChange = (_productIndex: number, _colorName: string) => {
    // In the future, we could update product images based on the selected color
  };

  // Email validation function
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email input change with validation
  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  // Handle feedback form submission
  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!feedbackText.trim()) {
      return;
    }

    // Validate email if provided
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams({
        message: feedbackText
      });

      // Add email if provided
      if (email.trim()) {
        formData.append('email', email.trim());
      }

      const response = await fetch('https://api.getorchard.com/v1/app-compat/submit-feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();

        if (result.success) {
          setSubmitStatus('success');
          setFeedbackText('');
          setEmail('');
          setEmailError('');
        } else {
          throw new Error(result.message || 'Failed to submit feedback');
        }
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      // Handle CORS errors - assume success since message likely went through
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        setSubmitStatus('success');
        setFeedbackText('');
        setEmail('');
        setEmailError('');
      } else {
        setSubmitStatus('error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="comparison-grid-container">
      {/* Empty spacer for grid alignment */}
      <div className="grid-cell feature-label-spacer"></div>

      {paddedProducts.map((product, index) => {
        return (
          <div className="grid-cell product-presentation" key={`product-${index}`}>
            {product?.name ? (
              <ColorVariantSlideshow
                defaultImage={product.image}
                productName={product.name}
                colorOptions={product.colorOptions}
                onColorChange={(colorName) => handleColorChange(index, colorName)}
              />
            ) : (
              <div className="empty-product-slot">
                {/* Empty slot - could add "Select a product" text here later */}
              </div>
            )}
          </div>
        );
      })}

      <div className="grid-cell feature-name">Price</div>
      {(() => {
        // Extract all unique storage options across all products
        const allStorageOptions = Array.from(
          new Set(
            paddedProducts.flatMap(product =>
              product ? product.price.map(p => p.storage) : []
            )
          )
        ).sort((a, b) => {
          // Custom sort function to handle storage sizes properly
          // Extract numeric values from storage options (e.g., "128GB" -> 128)
          const aNum = parseInt(a.match(/\d+/)?.[0] || '0');
          const bNum = parseInt(b.match(/\d+/)?.[0] || '0');
          return aNum - bNum; // Sort numerically
        });

        // Return the updated JSX with all storage options for each product
        return paddedProducts.map((product, index) => (
          <div className="grid-cell product-data" key={`price-${index}`}>
            <div className="price-options-container">
              {product ? allStorageOptions.map((storageOption, i) => {
                // Find if this product has this storage option
                const priceData = product.price.find(p => p.storage === storageOption);

                // Only render if this product has this storage option
                if (priceData) {
                  return (
                    <div className="price-option" key={i}>
                      <div className="storage-label">{storageOption}</div>
                      <div className="price-label">from <span className="price-amount">${
                        typeof priceData.price === 'string' ?
                          Math.round(parseFloat(priceData.price)).toString() :
                          Math.round(priceData.price).toString()
                      }</span></div>
                    </div>
                  );
                }
                return null;
              }) : null}
            </div>
          </div>
        ));
      })()}

      <div className="grid-cell feature-name">Navigation</div>
      {paddedProducts.map((product, index) => (
        <div className="grid-cell product-data" key={`nav-${index}`}>
          {product?.navigation || '\u00A0'}
        </div>
      ))}

      {/* OS Support row */}
      <div className="grid-cell feature-name">
        OS Support
        <InfoPopup
          id="ios-support-info"
          content="When a phone is no longer supported by Apple, core features like browser, calling, iMessage, camera etc... will continue to work but apps developed by other companies (ie Google Maps, Uber) may not work."
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
              <mask id="mask0_3308_253" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="15" height="15">
                <path d="M0 0H15V14.9774H0V0Z" fill="white" />
              </mask>
              <g mask="url(#mask0_3308_253)">
                <path d="M7.5 0C3.36439 0 0 3.35945 0 7.48871C0 11.6181 3.36439 14.9774 7.5 14.9774C11.6355 14.9774 15 11.6181 15 7.48871C15 3.35945 11.6356 0 7.5 0ZM7.5 13.5281C4.16488 13.5281 1.45154 10.8188 1.45154 7.48871C1.45154 4.15861 4.16488 1.44949 7.5 1.44949C10.8351 1.44949 13.5483 4.15861 13.5483 7.48871C13.5483 10.8188 10.8351 13.5281 7.5 13.5281Z" fill="#0187A7" />
              </g>
              <path d="M7.49927 5.12158C7.09831 5.12158 6.77344 5.44623 6.77344 5.84633V11.1963C6.77344 11.5964 7.09831 11.921 7.49927 11.921C7.90023 11.921 8.22511 11.5964 8.22511 11.1963V5.84633C8.22511 5.44623 7.90023 5.12158 7.49927 5.12158Z" fill="#0187A7" />
              <path d="M7.49927 3.05664C7.09831 3.05664 6.77344 3.38129 6.77344 3.78138V3.78372C6.77344 4.18382 7.09831 4.50743 7.49927 4.50743C7.90023 4.50743 8.22511 4.18148 8.22511 3.78138C8.22511 3.38129 7.90023 3.05664 7.49927 3.05664Z" fill="#0187A7" />
            </svg>
          }
        />
      </div>
      {paddedProducts.map((product, index) => (
        <div className="grid-cell product-data" key={`ios-${index}`}>
          {product?.iosSupport?.releaseYear && (
            <>Release Year: {product.iosSupport.releaseYear}<br /></>
          )}
          {product?.iosSupport?.latestIOS && (
            <>Latest compatible OS: {product.iosSupport.latestIOS}<br /></>
          )}
          {product?.iosSupport?.yearsUntilEndOfSupport && (
            <>Supported by {product.name.toLowerCase().includes('iphone') ? 'Apple' : 'Android'} until {product.iosSupport.yearsUntilEndOfSupport}</>
          )}
          {!product && '\u00A0'}
        </div>
      ))}

      <div className="grid-cell feature-name">Phone Size</div>
      {paddedProducts.map((product, index) => (
        <div className="grid-cell product-data" key={`size-${index}`}>
          {product?.phoneSize || '\u00A0'}
        </div>
      ))}

      <div className="grid-cell feature-name">Screen Size</div>
      {paddedProducts.map((product, index) => (
        <div className="grid-cell product-data" key={`screen-${index}`}>
          {product?.screenSize || '\u00A0'}
        </div>
      ))}

      <div className="grid-cell feature-name">Camera</div>
      {paddedProducts.map((product, index) => (
        <div className="grid-cell product-data" key={`camera-specs-${index}`}>
          {product?.cameraSpecs?.frontCamera && product.cameraSpecs.frontCamera.trim() && (
            <><strong>Front Camera:</strong> {product.cameraSpecs.frontCamera}<br /></>
          )}
          {product?.cameraSpecs?.rearCamera && product.cameraSpecs.rearCamera.trim() && (
            <><strong>Rear Camera:</strong> {product.cameraSpecs.rearCamera}<br /></>
          )}
          {product?.cameraSpecs?.opticalZoom && product.cameraSpecs.opticalZoom.trim() && (
            <><strong>Optical Zoom:</strong> {product.cameraSpecs.opticalZoom}<br /></>
          )}
          {product?.cameraSpecs?.slowMotionVideo && product.cameraSpecs.slowMotionVideo.trim() && (
            <><strong>Slow Motion Video:</strong> {product.cameraSpecs.slowMotionVideo}</>
          )}
          {(!product?.cameraSpecs?.frontCamera || !product.cameraSpecs.frontCamera.trim()) &&
            (!product?.cameraSpecs?.rearCamera || !product.cameraSpecs.rearCamera.trim()) &&
            (!product?.cameraSpecs?.opticalZoom || !product.cameraSpecs.opticalZoom.trim()) &&
            (!product?.cameraSpecs?.slowMotionVideo || !product.cameraSpecs.slowMotionVideo.trim()) &&
            '\u00A0'
          }
        </div>
      ))}

      <div className="grid-cell feature-name">Battery Life</div>
      {paddedProducts.map((product, index) => (
        <div className="grid-cell product-data" key={`battery-${index}`}>
          {product?.batteryLife || '\u00A0'}
        </div>
      ))}

      <div className="grid-cell feature-name">
        Processor
        <InfoPopup
          content={
            <>
              The chip makes everything happen, from opening apps to taking photos, from running games to saving battery.
              <br /><br />
              It's composed of:
              <br /><br />
              <strong>CPU</strong> – The manager, handling everyday tasks like messaging, web browsing, or checking the
              weather.
              <br /><br />
              <strong>GPU</strong> – The artist/muscle, taking care of graphics, animations, and games.
              <br /><br />
              If you're just using messages or browsing, the CPU does most of the work. But if you're gaming or editing videos,
              the GPU kicks in and does the heavy lifting.
            </>
          }
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
              <mask id="mask0_3308_253" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="15" height="15">
                <path d="M0 0H15V14.9774H0V0Z" fill="white" />
              </mask>
              <g mask="url(#mask0_3308_253)">
                <path d="M7.5 0C3.36439 0 0 3.35945 0 7.48871C0 11.6181 3.36439 14.9774 7.5 14.9774C11.6355 14.9774 15 11.6181 15 7.48871C15 3.35945 11.6356 0 7.5 0ZM7.5 13.5281C4.16488 13.5281 1.45154 10.8188 1.45154 7.48871C1.45154 4.15861 4.16488 1.44949 7.5 1.44949C10.8351 1.44949 13.5483 4.15861 13.5483 7.48871C13.5483 10.8188 10.8351 13.5281 7.5 13.5281Z" fill="#0187A7" />
              </g>
              <path d="M7.49927 5.12158C7.09831 5.12158 6.77344 5.44623 6.77344 5.84633V11.1963C6.77344 11.5964 7.09831 11.921 7.49927 11.921C7.90023 11.921 8.22511 11.5964 8.22511 11.1963V5.84633C8.22511 5.44623 7.90023 5.12158 7.49927 5.12158Z" fill="#0187A7" />
              <path d="M7.49927 3.05664C7.09831 3.05664 6.77344 3.38129 6.77344 3.78138V3.78372C6.77344 4.18382 7.09831 4.50743 7.49927 4.50743C7.90023 4.50743 8.22511 4.18148 8.22511 3.78138C8.22511 3.38129 7.90023 3.05664 7.49927 3.05664Z" fill="#0187A7" />
            </svg>
          }
        />
      </div>
      {paddedProducts.map((product, index) => (
        <div className="grid-cell product-data" key={`processor-${index}`}>
          {product?.processor || '\u00A0'}
        </div>
      ))}

      <div className="grid-cell feature-name">SD Card Compatible</div>
      {paddedProducts.map((product, index) => (
        <div className="grid-cell product-data" key={`sd-${index}`}>
          {product ? product.sdCardCompatible : '\u00A0'}
        </div>
      ))}

      <div className="grid-cell feature-name">Apple Watch Support</div>
      {paddedProducts.map((product, index) => (
        <div className="grid-cell product-data" key={`watch-${index}`}>
          {product ? (
            <>
              {product.appleWatchSupport && (
                <>
                  <strong>Apple Watch:</strong> {product.appleWatchSupport}<br />
                </>
              )}
              {product.appleWatchSESupport && (
                <>
                  <strong>Apple Watch SE:</strong> {product.appleWatchSESupport}<br />
                </>
              )}
              {product.appleWatchUltraSupport && (
                <>
                  <strong>Apple Watch Ultra:</strong> {product.appleWatchUltraSupport}
                </>
              )}
              {!product.appleWatchSupport && !product.appleWatchSESupport && !product.appleWatchUltraSupport && 'No'}
            </>
          ) : (
            '\u00A0'
          )}
        </div>
      ))}

      <div className="grid-cell feature-name">Charging</div>
      {paddedProducts.map((product, index) => (
        <div className="grid-cell product-data" key={`charging-${index}`}>
          {product?.charging && product.charging.length > 0 ? (
            product.charging.map((c, i) => (
              <React.Fragment key={i}>
                {c}
                {i < product.charging.length - 1 && <br />}
              </React.Fragment>
            ))
          ) : (
            '\u00A0'
          )}
        </div>
      ))}

      <div className="grid-cell feature-name">Connectivity</div>
      {paddedProducts.map((product, index) => (
        <div className="grid-cell product-data" key={`connectivity-${index}`}>
          {product?.connectivity && product.connectivity.length > 0 ? (
            <>
              {product.connectivity.map((c, i) => (
                <React.Fragment key={i}>
                  {c}
                  {i < product.connectivity.length - 1 && <br />}
                </React.Fragment>
              ))}
              {product.simCard && (
                <>
                  <br />
                  {product.simCard}
                </>
              )}
            </>
          ) : product?.simCard ? (
            product.simCard
          ) : (
            '\u00A0'
          )}
        </div>
      ))}

      {/* Feedback Row */}
      <div className="grid-cell feedback-row">
        <div className="feedback-title">
          Something missing or not right? Let us know below!
        </div>
        <form className="feedback-form" onSubmit={handleFeedbackSubmit}>
          <div className="feedback-input-container">
            <input
              type="text"
              className="feedback-input"
              name="feedback"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Hey Orchard, you made a mistake in this section..."
              maxLength={500}
              disabled={isSubmitting}
            />
            <input
              type="email"
              className="feedback-input email-input"
              name="email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              placeholder="Optional: enter your email here for a follow-up on your feedback"
              disabled={isSubmitting}
            />
            <div className="feedback-actions">
              <button
                type="submit"
                className="feedback-submit"
                disabled={!feedbackText.trim() || isSubmitting || feedbackText.length >= 500}
              >
                {isSubmitting ? 'Sending...' : 'Send Feedback'}
              </button>
              <div className="validation-container">
                {feedbackText.length > 300 && (
                  <div className="character-count">
                    <span className={feedbackText.length > 450 ? 'warning' : ''}>
                      {feedbackText.length}/500 characters
                    </span>
                  </div>
                )}
                {emailError && (
                  <div className="email-error">
                    {emailError}
                  </div>
                )}
              </div>
            </div>
          </div>
          {submitStatus === 'success' && (
            <div className="feedback-message feedback-success">
              Thank you for your feedback! We'll review it and make improvements.
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="feedback-message feedback-error">
              Sorry, there was an error submitting your feedback. Please try again.
            </div>
          )}
        </form>
      </div>

    </div>
  );
};

export default ComparisonTable;
